import { ensureExhaustive } from "@b2bportal/core-utilities";
import {
  FuelType,
  type GroundSupplier,
  type PickUpType,
  TransmissionType,
  type VehicleAvailability,
  VehicleCategory,
  VehicleType,
} from "@b2bportal/ground-api";
import dayjs from "dayjs";
import {
  type CarTypeFilter,
  PassengerFilter,
  type PriceRange,
  type Specification,
} from "..";

export const performCarTypeFilter = (
  vehicle: VehicleAvailability,
  carTypeFilter: CarTypeFilter[]
): boolean => {
  if (carTypeFilter.length > 0) {
    return carTypeFilter.some((typeFilter) => {
      return matchesCarType({
        ...CAR_TYPE_CONFIGS[typeFilter],
        car: vehicle,
      });
    });
  }

  return true;
};

export const performPassengersFilter = (
  vehicle: VehicleAvailability,
  passengerFilter: PassengerFilter[]
): boolean => {
  if (passengerFilter.length > 0) {
    const vehiclePassengers = vehicle.vehicle.passengerQty;
    if (
      passengerFilter.includes(PassengerFilter.UpToFive) &&
      vehiclePassengers <= 5
    ) {
      return true;
    }
    if (
      passengerFilter.includes(PassengerFilter.SixOrMore) &&
      vehiclePassengers >= 6
    ) {
      return true;
    }
    return false;
  }
  return true;
};

export const performPriceRangeFilter = (
  vehicle: VehicleAvailability,
  priceRange: PriceRange | null
): boolean => {
  if (
    priceRange != null &&
    (vehicle.totalCharge.fiat.value < priceRange.min ||
      vehicle.totalCharge.fiat.value > priceRange.max)
  ) {
    return false;
  }

  return true;
};

export const performMaxTotalPriceFilter = (
  vehicle: VehicleAvailability,
  maxTotalPrice: number
): boolean =>
  maxTotalPrice != null ? vehicle.payNow.fiat.value <= maxTotalPrice : true;

export const performCarCompaniesFilter = (
  vehicle: VehicleAvailability,
  suppliers: GroundSupplier[]
): boolean => {
  const vehicleSupplier = vehicle.supplierRef;
  if (suppliers.length === 0) return true;
  return suppliers.find((s) => s.code === vehicleSupplier) != null;
};

export const performSpecificationsFilter = (
  vehicle: VehicleAvailability,
  specifications: Specification
): boolean => {
  return Object.entries(specifications).every(([specification, isSelected]) => {
    if (!isSelected) return true;

    switch (specification as keyof Specification) {
      case "unlimitedMileage":
        return vehicle.hasUnlimitedMileage;
      case "automaticTransmission":
        return vehicle.vehicle.transmissionType === TransmissionType.Automatic;
      case "airCondition":
        return vehicle.vehicle.airConditioning;
      default:
        return true;
    }
  });
};

export const performCancellationPolicyFilter = (
  vehicle: VehicleAvailability,
  freeCancellation: boolean
): boolean => {
  if (!freeCancellation) return true;

  const { RestrictionCancel } = vehicle.cancellationPolicy;
  const pickUpTime = dayjs(vehicle.pickUp.time);

  switch (RestrictionCancel) {
    case "Conditional":
      // TODO: remove time calculation on the frontend
      return pickUpTime
        .subtract(vehicle.cancellationPolicy.hoursBeforePickUp ?? 0, "h")
        .isAfter(dayjs());

    case "ConditionalAfterBooking":
    case "NonRefundable":
    case "Paid":
    case "SteppedCancellationRule":
      return false;

    case "Free":
      return true;

    default:
      ensureExhaustive(RestrictionCancel);
  }
};

export const performPickUpTypeFilter = (
  vehicle: VehicleAvailability,
  pickUpType: PickUpType[]
): boolean => {
  if (pickUpType.length > 0) {
    const vehiclePickUpType = vehicle.pickUp.pickUpInformation?.pickUpType;
    if (vehiclePickUpType && pickUpType.includes(vehiclePickUpType)) {
      return true;
    }
    return false;
  }
  return true;
};

export const performBagsFilter = (
  vehicle: VehicleAvailability,
  minimumBags: number
): boolean => {
  if (minimumBags) {
    const vehicleBagCapacity = vehicle.vehicle.bagsInfo["Unspecified"] ?? null;
    if (vehicleBagCapacity && minimumBags <= vehicleBagCapacity) {
      return true;
    }
    return false;
  }
  return true;
};

const CAR_TYPE_CONFIGS = {
  CompactCar: {
    categoryList: [VehicleCategory.Compact, VehicleCategory.CompactElite],
    typeList: [VehicleType.Car],
  },
  CompactSUV: {
    categoryList: [VehicleCategory.Compact, VehicleCategory.CompactElite],
    typeList: [VehicleType.SUV],
  },
  Convertible: {
    typeList: [VehicleType.Convertible],
  },
  EconomyCar: {
    categoryList: [VehicleCategory.Economy, VehicleCategory.EconomyElite],
    typeList: [VehicleType.Car],
  },
  EconomySUV: {
    categoryList: [VehicleCategory.Economy, VehicleCategory.EconomyElite],
    typeList: [VehicleType.SUV],
  },
  Electric: {
    fuelList: [FuelType.Electric],
  },
  FullSizeCar: {
    categoryList: [VehicleCategory.Fullsize, VehicleCategory.FullsizeElite],
    typeList: [VehicleType.Car],
  },
  FullSizeSUV: {
    categoryList: [VehicleCategory.Fullsize, VehicleCategory.FullsizeElite],
    typeList: [VehicleType.SUV],
  },
  Hybrid: {
    fuelList: [FuelType.Hybrid],
  },
  LuxuryCar: {
    categoryList: [VehicleCategory.Luxury, VehicleCategory.LuxuryElite],
    typeList: [VehicleType.Car],
  },
  LuxurySUV: {
    categoryList: [VehicleCategory.Fullsize, VehicleCategory.FullsizeElite],
    typeList: [VehicleType.SUV],
  },
  MidSizeCar: {
    categoryList: [
      VehicleCategory.Intermediate,
      VehicleCategory.IntermediateElite,
    ],
    typeList: [VehicleType.Car],
  },
  MidSizeSUV: {
    categoryList: [
      VehicleCategory.Intermediate,
      VehicleCategory.IntermediateElite,
    ],
    typeList: [VehicleType.SUV],
  },
  MiniCar: {
    categoryList: [VehicleCategory.Mini, VehicleCategory.MiniElite],
    typeList: [VehicleType.Car],
  },
  MinivanVan: {
    typeList: [VehicleType.PassengerVan, VehicleType.CommercialVanTruck],
  },
  PickupTruck: {
    typeList: [
      VehicleType.PickUpDoubleCab,
      VehicleType.PickUpSingleExtendedCar,
    ],
  },
  PremiumCar: {
    categoryList: [VehicleCategory.Premium, VehicleCategory.PremiumElite],
    typeList: [VehicleType.Car],
  },
  PremiumSUV: {
    categoryList: [VehicleCategory.Premium, VehicleCategory.PremiumElite],
    typeList: [VehicleType.SUV],
  },
  RecreationalVehicle: {
    typeList: [
      VehicleType.OpenAirAllTerrain,
      VehicleType.Monospace,
      VehicleType.RecreationalVehicle,
      VehicleType.MotorHome,
      VehicleType.TwoWheelVehicle,
    ],
  },
  SportsCar: {
    typeList: [VehicleType.SportsCar, VehicleType.Roadster],
  },
  StandardCar: {
    categoryList: [VehicleCategory.Standard, VehicleCategory.StandardElite],
    typeList: [VehicleType.Car],
  },
  StandardSUV: {
    categoryList: [VehicleCategory.Standard, VehicleCategory.StandardElite],
    typeList: [VehicleType.SUV],
  },
  MysteryCar: {
    categoryList: [VehicleCategory.Special],
    typeList: [VehicleType.SpecialCar],
  },
} satisfies {
  [K in CarTypeFilter]: {
    categoryList?: VehicleCategory[];
    typeList?: VehicleType[];
    fuelList?: FuelType[];
  };
};

const matchesCarType = ({
  categoryList = [],
  typeList = [],
  fuelList = [],
  car,
}: {
  categoryList?: VehicleCategory[];
  typeList?: VehicleType[];
  fuelList?: FuelType[];
  car: VehicleAvailability;
}): boolean => {
  const matchesCategory =
    categoryList.length === 0 ||
    (car.vehicle.category != null &&
      categoryList.includes(car.vehicle.category));

  const matchesType =
    typeList.length === 0 ||
    (car.vehicle.vehicleType != null &&
      typeList.includes(car.vehicle.vehicleType));

  const matchesFuel =
    fuelList.length === 0 ||
    (car.vehicle.fuelType != null && fuelList.includes(car.vehicle.fuelType));

  return matchesCategory && matchesType && matchesFuel;
};
